/*
 * /auth/diagnoses (page)
 */
import {
	Badge,
	Button,
	cn,
	Container,
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Form,
	InputFormField,
	PageHeader,
	PageWithEffect,
	TextareaFormField,
	toast,
} from "@emr/react-ui"
import type { Diagnosis } from "@emr/db/client"
import { createFileRoute } from "@tanstack/react-router"
import { CheckCircle, Pencil, Plus, ToggleLeft, ToggleRight, XCircle } from "lucide-react"
import { useState } from "react"
import { z } from "zod"
import { trpc } from "../../trpc"
import { adminPageLoader } from "../../utils/auth.utils"

export const Route = createFileRoute("/auth/diagnoses")({
	component: DiagnosesPage,
	loader: adminPageLoader,
})

const diagnosisSchema = z.object({
	name: z.string().min(1, "Name is required"),
	description: z.string().optional(),
})
type DiagnosisValues = z.infer<typeof diagnosisSchema>

function DiagnosesPage() {
	const [editDiagnosis, setEditDiagnosis] = useState<Diagnosis | null>(null)
	const [createOpen, setCreateOpen] = useState(false)

	const { data, isLoading, refetch } = trpc.diagnosis.getAllByFilter.useQuery({})
	const diagnoses = data ?? []

	const create = trpc.diagnosis.create.useMutation({
		onSuccess: () => { toast.success("Diagnosis created"); refetch(); setCreateOpen(false) },
		onError: (e) => toast.error(e.message),
	})
	const update = trpc.diagnosis.update.useMutation({
		onSuccess: () => { toast.success("Diagnosis updated"); refetch(); setEditDiagnosis(null) },
		onError: (e) => toast.error(e.message),
	})

	function toggleActive(d: Diagnosis) {
		update.mutate({ id: d.id, active: !d.active })
	}

	return (
		<PageWithEffect effect="aurora" centerVertical={false}>
			<Container width="xl" padding="lg" gap="lg">
				<PageHeader
					title="Diagnoses"
					right={{
						Component: (
							<Button size="sm" onClick={() => setCreateOpen(true)}>
								<Plus className="w-4 h-4 mr-1.5" /> Add Diagnosis
							</Button>
						),
					}}
				/>

				{isLoading ? (
					<p className="text-sm text-muted-foreground">Loading…</p>
				) : diagnoses.length === 0 ? (
					<p className="text-sm text-muted-foreground">No diagnoses yet.</p>
				) : (
					<div className="rounded-lg border divide-y">
						{diagnoses.map((d) => (
							<div key={d.id} className="flex items-center gap-3 px-4 py-3">
								<div className="flex-1 min-w-0">
									<div className="flex items-center gap-2">
										<p className="text-sm font-medium">{d.name}</p>
										<Badge variant={d.active ? "secondary" : "outline"} className="text-xs">
											{d.active ? (
												<><CheckCircle className="w-3 h-3 mr-1" />Active</>
											) : (
												<><XCircle className="w-3 h-3 mr-1" />Inactive</>
											)}
										</Badge>
									</div>
									{d.description && (
										<p className="text-xs text-muted-foreground mt-0.5">{d.description}</p>
									)}
								</div>
								<div className="flex items-center gap-2 shrink-0">
									<Button variant="ghost" size="sm" onClick={() => setEditDiagnosis(d)}>
										<Pencil className="w-3.5 h-3.5 mr-1" /> Edit
									</Button>
									<Button
										variant="ghost"
										size="sm"
										onClick={() => toggleActive(d)}
										className={cn(d.active ? "text-muted-foreground" : "text-green-600")}
									>
										{d.active ? (
											<><ToggleLeft className="w-4 h-4 mr-1" />Deactivate</>
										) : (
											<><ToggleRight className="w-4 h-4 mr-1" />Activate</>
										)}
									</Button>
								</div>
							</div>
						))}
					</div>
				)}
			</Container>

			{/* Create dialog */}
			<Dialog open={createOpen} onOpenChange={setCreateOpen}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Add Diagnosis</DialogTitle>
					</DialogHeader>
					<Form<DiagnosisValues>
						schema={diagnosisSchema}
						onSubmit={async (v) => { create.mutate({ name: v.name, description: v.description || undefined }) }}
						className="flex flex-col gap-4"
					>
						<InputFormField name="name" label="Name" required />
						<TextareaFormField name="description" label="Description" />
						<DialogFooter>
							<Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
							<Button type="submit" disabled={create.isPending}>
								{create.isPending ? "Saving…" : "Create"}
							</Button>
						</DialogFooter>
					</Form>
				</DialogContent>
			</Dialog>

			{/* Edit dialog */}
			{editDiagnosis && (
				<Dialog open onOpenChange={(o) => { if (!o) setEditDiagnosis(null) }}>
					<DialogContent className="sm:max-w-md">
						<DialogHeader>
							<DialogTitle>Edit Diagnosis</DialogTitle>
						</DialogHeader>
						<Form<DiagnosisValues>
							schema={diagnosisSchema}
							defaultValues={{ name: editDiagnosis.name, description: editDiagnosis.description ?? "" }}
							onSubmit={async (v) => { update.mutate({ id: editDiagnosis.id, name: v.name, description: v.description || undefined }) }}
							className="flex flex-col gap-4"
						>
							<InputFormField name="name" label="Name" required />
							<TextareaFormField name="description" label="Description" />
							<DialogFooter>
								<Button type="button" variant="outline" onClick={() => setEditDiagnosis(null)}>Cancel</Button>
								<Button type="submit" disabled={update.isPending}>
									{update.isPending ? "Saving…" : "Save"}
								</Button>
							</DialogFooter>
						</Form>
					</DialogContent>
				</Dialog>
			)}
		</PageWithEffect>
	)
}
